import type { ISamplePool } from '../../../Model/SamplePool/ISamplePool.js'
import type { IProjectSample } from '../../../Model/Project/IProjectSample.js'

export interface ImportableSummary {
  /** Samples in the order, negative controls excluded. */
  total: number
  importable: number
  alreadyInProject: number
  /** Never offered for import. */
  negativeControls: number
}

export const summariseImportable = (pools: ISamplePool[], projectSampleIds: ReadonlySet<string>): ImportableSummary => {
  const samples = pools.flatMap((pool) => pool.samples)
  const alreadyInProject = samples.filter((sample) => projectSampleIds.has(sample.id)).length
  return {
    total: samples.length,
    importable: samples.length - alreadyInProject,
    alreadyInProject,
    negativeControls: pools.reduce((sum, pool) => sum + (pool.negativeControls?.length ?? 0), 0)
  }
}

const plural = (n: number, singular: string, pluralForm = `${singular}s`) => `${n} ${n === 1 ? singular : pluralForm}`

export const importableMessage = (summary: ImportableSummary): string => {
  const reasons: string[] = []
  if (summary.alreadyInProject > 0) reasons.push(`${summary.alreadyInProject} already in this project`)
  if (summary.negativeControls > 0) reasons.push(`${plural(summary.negativeControls, 'negative control')} not importable`)

  const head =
    summary.total === 0
      ? 'This order has no samples'
      : summary.importable === summary.total
        ? `${plural(summary.total, 'sample')} importable`
        : `${summary.importable} of ${plural(summary.total, 'sample')} importable`
  return reasons.length > 0 ? `${head} — ${reasons.join(', ')}` : head
}

export const projectSampleIdSet = (samples: IProjectSample[] | 'LOADING'): ReadonlySet<string> =>
  new Set(samples === 'LOADING' ? [] : samples.map((x) => x.sampleId))

export const sampleIdsToAdd = (selected: Iterable<string>, inProject: ReadonlySet<string>): string[] =>
  [...new Set(selected)].filter((id) => !inProject.has(id))
